import { useCallback, useEffect, useRef } from 'react';

type UseProgrammaticScrollProps = {
  isLoading: boolean;
  isScrolling: boolean;
};

type UseProgrammaticScrollReturn = {
  messageContainerRef: React.RefObject<HTMLDivElement>;
  isProgrammaticScrollRef: React.MutableRefObject<boolean>;
};

export const useProgrammaticScroll = ({
  isLoading,
  isScrolling,
}: UseProgrammaticScrollProps): UseProgrammaticScrollReturn => {
  const messageContainerRef = useRef<HTMLDivElement | null>(null);
  const isProgrammaticScrollRef = useRef(false);
  const scrollTimeoutRef = useRef<number | null>(null);

  const clearScrollTimeout = () => {
    if (scrollTimeoutRef.current != null) {
      window.clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
  };

  const scrollToBottom = useCallback(() => {
    // If the user intentionally scrolled away, do nothing.
    if (isScrolling) return;

    const container = messageContainerRef.current;
    if (!container) return;

    // verify that the user is near the bottom (within 200px) to prevent overriding user scroll
    if (container.scrollHeight - container.scrollTop - container.clientHeight < 200) {
      // mark programmatic scroll so scroll event handlers can ignore it
      isProgrammaticScrollRef.current = true;
      clearScrollTimeout();

      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight - container.clientHeight;
        isProgrammaticScrollRef.current = false;
        scrollTimeoutRef.current = null;
      });
    }
  }, [isScrolling]);

  useEffect(() => {
    const container = messageContainerRef.current;
    if (!container) return;

    // ResizeObserver -> triggers when container's size/layout changes
    const resizeObs = new ResizeObserver(() => {
      if (isScrolling) return;
      if (isProgrammaticScrollRef.current) return;
      scrollToBottom();
    });
    resizeObs.observe(container);

    // MutationObserver -> fires for DOM changes (useful for streaming text)
    const mutationObs = new MutationObserver(() => {
      if (isScrolling) return;
      if (isProgrammaticScrollRef.current) return;

      scrollToBottom();
    });
    mutationObs.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      resizeObs.disconnect();
      mutationObs.disconnect();
      clearScrollTimeout();
      isProgrammaticScrollRef.current = false;
    };
  }, [scrollToBottom, isScrolling]);

  useEffect(() => {
    if (isLoading) scrollToBottom();
  }, [isLoading, scrollToBottom]);

  return {
    messageContainerRef,
    isProgrammaticScrollRef,
  };
};
